<template>
  <div class="bg-main h-full md:pt-12">
    <BreadCrumbs v-if="!uiStore.isMobile && !isClubSeller" />
    <div v-if="isClubSeller" class="container">
      {{ $lang('seller', 'giftcard_not_available') }}
    </div>
    <div v-else class="container xxl:px-24 md:px-56 px-0 md:flex">
      <div class="productImages md:mx-0 order-1 md:order-2 w-screen md:w-2/3 md:pr-56 relative">
        <div class="">
          <nuxt-img
            :src="page.giftcard_image"
            preset="standard"
            sizes="mob375:100vw mob390:100vw mob414:100vw desk:817px"
            alt=""
          />
        </div>
      </div>
      <div class="productInformation order-3 pt-16 relative md:pt-0 md:w-1/3 m-24 md:m-0">
        <div
          class="
          inline-block
          bg-black-25
          text-black-75 text-xs
          rounded-regular
          w-fit-content
          py-4
          px-8
          mb-12
          mr-8
          font-medium"
        >
          {{ $lang('product_page', 'gift_card_new_flag') }}
        </div>
        <div class="text-product-large">{{ page.title }}</div>
        <div
          v-if="page.content"
          class="text-small text-muted mt-8 mb-24 wp-content"
          v-html="page.content"
        />
        <div>
          <div class="text-small-medium font-sans">
            {{ $lang('product_page', 'gift_card_quantity_label') }}
          </div>
          <div class="flex flex-wrap mt-4">
            <div
              v-for="item in filteredItems"
              :key="item.giftCertificate"
              class="w-1/3 mb-8 px-4"
            >
              <TabSelector
                class="px-1"
                :text="item.name"
                :selected="selectedItem === item.giftCertificate"
                fullwidth
                @click="selectedItem = item.giftCertificate"
              />
            </div>
          </div>
          <ButtonComponent
            :loading="loadingStatus === 'LOADING'"
            is-block
            class="w-full mt-16"
            :text="$lang('product_page', 'add_to_cart_action')"
            @click="attemptAddToCart"
          />
        </div>
      </div>
    </div>
    <GiftCardClearCart
      v-if="modalOpen"
      :loading="loadingStatus === 'LOADING'"
      @close="modalOpen = false"
      @clear="clearAndAddToCart"
    />
  </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import { type GiftCertficate, type GiftCertficateApiResponse } from '~/constants/types/gift-certficate';
import { useCartStore } from '~/store/cart';
import { useUiStore } from '~/store/ui';
import BreadCrumbs from '~/components/productpage/BreadCrumbs.vue';
import TabSelector from '~/components/generic/TabSelector.vue';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import GiftCardClearCart from '~/components/cart/GiftCardClearCart.vue';
import type { SellerClub } from '~/constants/types/seller';

const uiStore = useUiStore();
const cartStore = useCartStore();
const { centraGet } = useApiFetch();

defineProps<{
  page: object,
  hasSiteWideMessage: boolean
}>();

const items = ref<GiftCertficate[]>([]);
const selectedItem = ref('');
const loadingStatus = ref('INIT');
const modalOpen = ref(false);
const { sellerCookieOptions } = useCookieConfig();

const clubCookie = useCookie<SellerClub>('club', sellerCookieOptions) as Ref<SellerClub | null | undefined>;

const isClubSeller = computed(()=> {
  if (clubCookie.value) {
    return true;
  }
  return false;
});

onMounted(async()=> {
  const res = await centraGet<GiftCertficateApiResponse>('/gift-certificates');
  if (res) {
    items.value = res.giftCertificates;
  } else {
    console.error(res);
  }
});

const filteredItems = computed<GiftCertficate[]>(()=>{
  return items.value.filter(cert => cert.amountByCurrency.find(amount => amount.currency === cartStore.cartData.currency))
    .sort((a, b) => a.amountByCurrency[0].amountAsNumber - b.amountByCurrency[0].amountAsNumber);
});

const addToCart = async() => {
  modalOpen.value = false;
  if (selectedItem.value !== '') {
    loadingStatus.value = 'LOADING';
    // Find the full gift certificate object
    const giftCertObj = filteredItems.value.find(item => item.giftCertificate === selectedItem.value);
    const res = await cartStore.addGiftCardToCart(selectedItem.value, giftCertObj);
    if (res) {
      setTimeout(()=> {
        loadingStatus.value = 'SUCCESS';
        setTimeout(() => (loadingStatus.value = 'INIT'), 2500);
      }, 1000);
    } else {
      loadingStatus.value = 'ERROR';
    }
  }
};

const attemptAddToCart = () => {
  if (!cartStore.cartData.isGiftCardCheckout && cartStore.cartData.items.length > 0) {
    modalOpen.value = true;
  } else {
    addToCart();
  }
};

const clearAndAddToCart = () => {
  console.log('clearAndAddToCart');
  addToCart();
};

</script>

<style scoped lang="postcss">
</style>
